import {
  formatDistance,
  isMetric,
} from "@/components/common/settings-context/distance-unit-utils";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import {
  PointCloudAnalysis,
  PointCloudAnalysisColormap,
  ReferencePlaneType,
} from "@/store/point-cloud-analysis-tool-slice";
import {
  ColorBar,
  ColorsWithRatio,
  FaroText,
  grey,
  NO_TRANSLATE_CLASS,
} from "@faro-lotv/flat-ui";
import { DistanceUnitTypes } from "@faro-lotv/foundation";
import { Box, Stack } from "@mui/system";
import { useMemo, type JSX } from "react";
import { ColorManagement } from "three";
import {
  CAD_UNMATCHED_COLOUR,
  cadScaleColours,
  isCadDeviationColouring,
  type CadDeviationSettings,
} from "@/tools/cad-analysis/cad-deviation-colouring";
import PaletteIcon from "@mui/icons-material/Palette";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Slider, Typography } from "@mui/material";

type ColorScaleProps = {
  /** The Analysis being rendered */
  analysis: PointCloudAnalysis;
  /**
   * If true, the scale will be rendered in dark mode.
   */
  dark?: boolean;
  /** Updates symmetric Bands thresholds; omitted for read-only/report legends. */
  onBandsChange?(tolerance: number, outerBandLimit: number): void;
  /** Updates the symmetric Continuous tolerance without changing the search range. */
  onToleranceChange?(tolerance: number): void;
  /** Prevent threshold editing when reference data or permissions are unavailable. */
  disabled?: boolean;
};

/**
 * @returns the color scale bar with the min and max color deviation labels.
 */
export function ColormapScale({
  analysis,
  dark,
  onBandsChange,
  onToleranceChange,
  disabled,
}: ColorScaleProps): JSX.Element {
  const colorBarColors = useMemo(
    () => computeColorBarColors(analysis.colormap),
    [analysis.colormap],
  );

  const scaleRange = useMemo(() => {
    if (analysis.referencePlaneType !== ReferencePlaneType.volume) {
      return [-analysis.tolerance, analysis.tolerance];
    }
    if (analysis.volumeAnalysisResult) {
      return [
        analysis.volumeAnalysisResult.heightMin,
        analysis.volumeAnalysisResult.heightMax,
      ];
    }
    return [0, 0];
  }, [
    analysis.referencePlaneType,
    analysis.tolerance,
    analysis.volumeAnalysisResult,
  ]);

  if (analysis.kind === "cad") {
    return (
      <CadDeviationScale
        analysis={analysis}
        dark={dark}
        onBandsChange={onBandsChange}
        onToleranceChange={onToleranceChange}
        disabled={disabled}
      />
    );
  }
  const continuous = (
    <Stack
      direction="row"
      spacing={1}
      sx={{ height: "100%", justifyContent: "center" }}
    >
      <ColorBar colors={colorBarColors} direction="vertical" />

      <ColorScaleLineIndicatorVertical
        maxValue={scaleRange[1]}
        minValue={scaleRange[0]}
        useLargeUnit={analysis.referencePlaneType === ReferencePlaneType.volume}
        dark={dark}
      />
    </Stack>
  );
  return continuous;
}

/** @returns The same deviation legend for draft settings, results and reports. */
export function CadDeviationScale({
  analysis,
  dark,
  onBandsChange,
  onToleranceChange,
  disabled,
}: {
  analysis: CadDeviationSettings;
  dark?: boolean;
  onBandsChange?(tolerance: number, outerBandLimit: number): void;
  onToleranceChange?(tolerance: number): void;
  disabled?: boolean;
}): JSX.Element {
  const distanceUnit = useDistanceUnit();
  const {
    tolerance,
    searchDistance: range,
    deviationColouring: colouring,
  } = analysis;
  const outer = colouring?.outerBandLimit ?? tolerance;
  const bands = colouring?.mode === "bands";
  const editable = bands ? !!onBandsChange : !!onToleranceChange;
  const colours = cadScaleColours(analysis);
  const ticks = [...new Set([range, range / 2, 0, -range / 2, -range])];
  const thresholds = bands
    ? [-outer, -tolerance, tolerance, outer]
    : [-tolerance, tolerance];
  const divisions = 1000;
  const step = range / divisions;
  function label(value: number): string {
    return `${value > 0 ? "+" : ""}${formatLabelText(value, distanceUnit, false)}`;
  }
  function position(value: number): number {
    return (range > 0 ? 0.5 + value / (2 * range) : 0.5) * 100;
  }
  function update(values: number | number[], active: number): void {
    if (!Array.isArray(values) || disabled) return;
    if (!bands) {
      const next = Math.min(
        range,
        Math.max(0, active === 0 ? -values[active] : values[active]),
      );
      onToleranceChange?.(next);
      return;
    }
    if (!onBandsChange) return;
    const magnitude = Math.max(
      0,
      active < 2 ? -values[active] : values[active],
    );
    let nextTolerance = tolerance;
    let nextOuter = outer;
    if (active === 1 || active === 2) {
      nextTolerance = Math.min(magnitude, outer - Math.min(step, outer / 2));
    } else {
      const gap = Math.min(step, (range - tolerance) / 2);
      nextOuter = Math.max(tolerance + gap, Math.min(range - gap, magnitude));
    }
    if (
      isCadDeviationColouring(
        { ...colouring, outerBandLimit: nextOuter },
        nextTolerance,
        range,
      )
    ) {
      onBandsChange(nextTolerance, nextOuter);
    }
  }
  return (
    <Stack
      spacing={1.5}
      aria-label="CAD deviation legend"
      sx={{
        color: dark ? grey[0] : grey[1000],
        width: 174,
        minWidth: 0,
        maxWidth: "100%",
        alignSelf: "stretch",
        pt: 1.5,
      }}
    >
      <Box
        role="group"
        aria-label="Signed deviation scale"
        sx={{
          position: "relative",
          flex: "1 0 280px",
          minHeight: 280,
          my: 1,
          ml: 1,
        }}
      >
        <Box
          role="img"
          aria-label="Deviation colour bar"
          sx={{ position: "absolute", top: 0, bottom: 0, left: 8, width: 16 }}
        >
          <ColorBar
            colors={colours}
            direction="vertical"
            sx={{ height: "100%" }}
          />
        </Box>
        {thresholds.map((value, index) => (
          <Box
            key={index}
            aria-hidden
            sx={{
              position: "absolute",
              left: 5,
              width: 22,
              bottom: `${position(value)}%`,
              borderTop: "1px solid",
              borderColor: dark ? grey[0] : grey[900],
              pointerEvents: "none",
            }}
          />
        ))}
        {ticks.map((value) => (
          <Stack
            key={value}
            direction="row"
            sx={{
              position: "absolute",
              left: 37,
              right: 0,
              bottom: `${position(value)}%`,
              transform: "translateY(50%)",
              alignItems: "center",
              gap: 0.75,
              pointerEvents: "none",
            }}
          >
            <Box
              sx={{
                width: 7,
                height: "1px",
                bgcolor: "currentColor",
                opacity: 0.6,
                flexShrink: 0,
              }}
            />
            <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
              {label(value)}
            </Typography>
          </Stack>
        ))}
        {range > 0 && editable && (
          <Slider
            key={bands ? "bands" : "continuous"}
            orientation="vertical"
            track={false}
            disableSwap
            min={-range}
            max={range}
            step={step}
            value={thresholds}
            disabled={disabled}
            getAriaLabel={(index) =>
              (bands
                ? [
                    "Negative outer band limit",
                    "Negative tolerance",
                    "Positive tolerance",
                    "Positive outer band limit",
                  ]
                : ["Negative tolerance", "Positive tolerance"])[index]
            }
            getAriaValueText={label}
            valueLabelFormat={label}
            valueLabelDisplay="auto"
            onChange={(_, values, active) => update(values, active)}
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 8,
              width: 16,
              height: "100%",
              p: 0,
              "& .MuiSlider-rail": { display: "none" },
              "& .MuiSlider-valueLabel": {
                left: "calc(100% + 8px)",
                right: "auto",
                top: "50%",
                bottom: "auto",
                transform: "translateY(-50%) scale(0)",
                transformOrigin: "left center",
                zIndex: 2,
                "&.MuiSlider-valueLabelOpen": {
                  transform: "translateY(-50%) scale(1)",
                },
                "&::before": {
                  left: 0,
                  top: "50%",
                  transform: "translate(-50%, -50%) rotate(45deg)",
                },
              },
              "& .MuiSlider-thumb": {
                width: 14,
                height: 14,
                bgcolor: dark ? grey[0] : grey[1000],
                border: "2px solid",
                borderColor: dark ? grey[900] : grey[0],
              },
              "& .MuiSlider-thumb[data-index='0'], & .MuiSlider-thumb[data-index='1']":
                { left: 0 },
              "& .MuiSlider-thumb[data-index='1']": {
                left: bands ? 0 : "100%",
              },
              "& .MuiSlider-thumb[data-index='2'], & .MuiSlider-thumb[data-index='3']":
                { left: "100%" },
            }}
          />
        )}
      </Box>
      {!editable && (
        <Stack spacing={0.25}>
          <Typography variant="caption">
            Tolerance: {"\u00b1"}
            {formatLabelText(tolerance, distanceUnit, false)}
          </Typography>
          {bands && (
            <Typography variant="caption">
              Outer limit: {"\u00b1"}
              {formatLabelText(outer, distanceUnit, false)}
            </Typography>
          )}
        </Stack>
      )}
      {analysis.deviationColouring && (
        <UnmatchedLegend analysis={analysis} dark={dark} />
      )}
    </Stack>
  );
}

function UnmatchedLegend({
  analysis,
  dark,
}: {
  analysis: CadDeviationSettings;
  dark?: boolean;
}): JSX.Element {
  const distanceUnit = useDistanceUnit();
  const hidden = analysis.deviationColouring?.unmatchedOpacity === 0;
  const icon = hidden ? (
    <VisibilityOffIcon
      titleAccess="Hidden unmatched points"
      sx={{ fontSize: 16, flexShrink: 0 }}
    />
  ) : (
    <PaletteIcon
      titleAccess="Source colours"
      sx={{ fontSize: 16, flexShrink: 0 }}
    />
  );
  return (
    <Stack
      direction="row"
      spacing={0.75}
      sx={{
        color: dark ? grey[0] : grey[1000],
        maxWidth: 190,
        alignItems: "center",
      }}
    >
      {!hidden && analysis.deviationColouring?.unmatched === "grey" ? (
        <Box
          sx={{
            backgroundColor: CAD_UNMATCHED_COLOUR,
            width: 14,
            height: 14,
            flexShrink: 0,
            border: "1px solid",
            borderColor: "divider",
          }}
        />
      ) : (
        icon
      )}
      <Typography variant="caption">
        Unmatched{hidden ? " (hidden)" : ""}: no usable CAD match within{" "}
        {formatLabelText(analysis.searchDistance, distanceUnit, false)}
      </Typography>
    </Stack>
  );
}

/**
 * @returns the string to be used for one graduated label
 * @param value value to display in meter
 * @param distanceUnit distance unit setting selected by user
 * @param useLargeUnit whether to use large unit (meters or feet) or small unit (millimeters or inches)
 */
function formatLabelText(
  value: number,
  distanceUnit: DistanceUnitTypes,
  useLargeUnit: boolean,
): string {
  const metric = isMetric(distanceUnit);

  return useLargeUnit
    ? formatDistance(
        value,
        metric ? DistanceUnitTypes.meters : DistanceUnitTypes.decimalFeet,
      )
    : formatDistance(
        value,
        metric
          ? DistanceUnitTypes.millimeters
          : DistanceUnitTypes.decimalInches,
      );
}

function computeColorBarColors(
  colormap: PointCloudAnalysisColormap,
): ColorsWithRatio {
  // The whole colormap range is mapped to the middle portion of the color bar.
  // The space before and after the whole range is padding with the minimal and maximal deviation color.
  // This constant defines padding ratio of the space before and after the color map range.
  const COLOR_SCALE_PADDING = 0.15;
  // scale the colormap range to middle portion of the color bar, with padding on both sides
  const colorRange = 1.0 - 2 * COLOR_SCALE_PADDING;

  // Colors supplied to three.js need to be in the Linear-sRGB. Certain conversions (for hexadecimal
  // and CSS colors in sRGB) can be made automatically if the THREE.ColorManagement API is enabled.
  // see: https://threejs.org/docs/?q=color#manual/en/introduction/Color-management
  // Enable the ColorManagement make sure `getHexString` return the hexadecimal color in sRGB for CSS.
  // However, the global flag `ColorManagement.enabled` may be set to `false` by the application for
  // certain reason, so we need to save and restore it.
  const colorManagementEnabled = ColorManagement.enabled;
  ColorManagement.enabled = true;

  const colors: ColorsWithRatio = colormap.map((colorKey) => ({
    color: colorKey.color,
    ratio: colorKey.value * colorRange + COLOR_SCALE_PADDING,
  }));

  ColorManagement.enabled = colorManagementEnabled;

  return [
    {
      color: colors[0].color,
      ratio: 0.0,
    },
    ...colors,
    {
      color: colors[colors.length - 1].color,
      ratio: 1.0,
    },
  ];
}

type VerticalColorScaleLineIndicatorProps = {
  /** Max deviation in meter */
  maxValue: number;

  /** Min deviation in meter */
  minValue: number;

  /** Use large unit for labels */
  useLargeUnit: boolean;

  /**
   * If true, the scale will be rendered in dark mode.
   */
  dark?: boolean;
};

/**
 * Vertical color scale line indicator of the min and max color deviations.
 *
 *  -- max
 *  -
 *  -  center
 *  -
 *  -- min
 *
 * The positions of the horizontal lines match the color bar ratios.
 *
 * @returns Color scale line indicator
 */
function ColorScaleLineIndicatorVertical({
  maxValue,
  minValue,
  dark,
  useLargeUnit,
}: VerticalColorScaleLineIndicatorProps): JSX.Element {
  const distanceUnit = useDistanceUnit();

  const labels = useMemo(() => {
    const step = (maxValue - minValue) / 4;
    return [
      formatLabelText(maxValue, distanceUnit, useLargeUnit),
      formatLabelText(maxValue - step, distanceUnit, useLargeUnit),
      formatLabelText(minValue + 2 * step, distanceUnit, useLargeUnit),
      formatLabelText(minValue + step, distanceUnit, useLargeUnit),
      formatLabelText(minValue, distanceUnit, useLargeUnit),
    ];
  }, [distanceUnit, maxValue, minValue, useLargeUnit]);

  const styleGrade = { opacity: 0.3 };

  const labelColor = dark ? grey[0] : grey[900];

  return (
    <Stack direction="row" sx={{ height: "250px" }}>
      {/** graduated line */}
      <Stack
        direction="column"
        sx={{ alignItems: "center", justifyContent: "center", ...styleGrade }}
      >
        <Box component="div" sx={{ height: "15%", width: "1px" }} />
        <Box
          component="div"
          sx={{ height: "1px", width: "10px", bgcolor: labelColor }}
        />
        <Box component="div" sx={{ height: "17%", width: "1px" }} />
        <Box
          component="div"
          sx={{ height: "1px", width: "8px", bgcolor: labelColor }}
        />
        <Box component="div" sx={{ height: "17%", width: "1px" }} />
        <Box
          component="div"
          sx={{ height: "1px", width: "8px", bgcolor: labelColor }}
        />
        <Box component="div" sx={{ height: "17%", width: "1px" }} />
        <Box
          component="div"
          sx={{ height: "1px", width: "8px", bgcolor: labelColor }}
        />
        <Box component="div" sx={{ height: "17%", width: "1px" }} />
        <Box
          component="div"
          sx={{ height: "1px", width: "10px", bgcolor: labelColor }}
        />
        <Box component="div" sx={{ height: "15%", width: "1px" }} />
      </Stack>

      {/** labels */}
      <Stack
        direction="column"
        spacing={4}
        sx={{ alignItems: "center", justifyContent: "center", margin: "4px" }}
      >
        {labels.map((label, index) => (
          <FaroText
            variant="bodyXS"
            className={NO_TRANSLATE_CLASS}
            color={labelColor}
            key={index}
            sx={{
              opacity:
                !dark || index === 0 || index === labels.length - 1 ? 0.5 : 1,
            }}
          >
            {label}
          </FaroText>
        ))}
      </Stack>
    </Stack>
  );
}
